import { useMutation, useQueryClient } from '@tanstack/react-query'
import { App } from 'antd'
import { useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate, useSearchParams } from 'react-router-dom'

import { AppLoadingScreen, ErrorBoundary } from 'src/components'
import { useSessionContext } from 'src/hooks'
import { paths } from 'src/router'
import {
  getSameOriginPathFromUrl,
  getSocialAuthRedirectOrigin,
  useShowApiErrors
} from 'src/utils'

import { useProviderCallback } from '../hooks'
import { type SocialProviderId, providerService } from '../providers'

type SocialConnectCallbackConfig = {
  provider: SocialProviderId
  flow: 'connect' | 'clientConnect' | 'orgConnect'
  redirectPath: string
  successMessageKey: string
}

export const SocialConnectCallbackPage = ({
  provider,
  flow,
  redirectPath,
  successMessageKey
}: SocialConnectCallbackConfig) => {
  const { message } = App.useApp()
  const showApiErrors = useShowApiErrors()
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()
  const { t } = useTranslation()
  const { user, isLoading } = useSessionContext()
  const userId = user?.user?.id
  const queryClient = useQueryClient()

  const { mutateAsync: linkProvider } = useMutation({
    mutationFn: providerService.processCallback
  })

  const navigateToReturnOrigin = useCallback(
    (returnOrigin: string | null) => {
      const sameOriginReturnPath = getSameOriginPathFromUrl(returnOrigin)
      if (sameOriginReturnPath) {
        window.location.assign(sameOriginReturnPath)
        return
      }

      if (!userId) {
        navigate(paths.home)
        return
      }

      navigate(paths.profile.connectedAccounts(String(userId)))
    },
    [navigate, userId]
  )

  const handleProcess = useCallback(
    async ({
      code,
      state,
      returnOrigin
    }: {
      code: string
      state: string
      returnOrigin: string | null
    }) => {
      try {
        await linkProvider({
          provider,
          flow,
          code,
          state,
          redirectUri: `${getSocialAuthRedirectOrigin()}${redirectPath}`
        })

        queryClient.invalidateQueries({ queryKey: ['linked-auth-methods'] })
        queryClient.invalidateQueries({ queryKey: ['org-linked-methods'] })
        message.success(t(successMessageKey))
      } catch (e) {
        showApiErrors(e)
      }

      navigateToReturnOrigin(returnOrigin)
    },
    [
      linkProvider,
      message,
      navigate,
      navigateToReturnOrigin,
      queryClient,
      showApiErrors,
      t,
      provider,
      flow,
      redirectPath,
      successMessageKey
    ]
  )

  useProviderCallback({
    provider,
    flow,
    searchParams,
    navigate,
    t,
    missingParamsKey: 'profileAuthMethods.connectCallbackMissingParams',
    fallbackPath: paths.auth.login,
    isBlocked: isLoading,
    onProcess: handleProcess
  })

  return (
    <ErrorBoundary>
      <AppLoadingScreen />
    </ErrorBoundary>
  )
}
